import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Add as AddIcon } from '@mui/icons-material';
import QuestionList from '../QuestionList';
import AddExerciseDialog from '../../Notebook/AddExerciseDialog';
import axios from '../../../api/axios';

function TeacherQuestionsTab({
  questions,
  categories,
  selectedNotebook,
  notebooks,
  loading,
  expandedSections,
  onSectionToggle,
  onViewStudents,
  onDelete,
  sortBy,
  onSortChange,
  selectedCourse,
  onQuestionClick,
  onQuestionUpdate = null
}) {
  const [notebookOutline, setNotebookOutline] = useState(null);
  const [sectionStats, setSectionStats] = useState({});
  const [questionStats, setQuestionStats] = useState({}); // 每题：几人做了、正确率（教师列表用）
  const [addExerciseDialogOpen, setAddExerciseDialogOpen] = useState(false);
  const [selectedSectionForMenu, setSelectedSectionForMenu] = useState(null);
  const [viewMode, setViewMode] = useState('section'); // 'section' | 'type' | 'difficulty'
  const [searchQuery, setSearchQuery] = useState('');
  const [difficultySnackbar, setDifficultySnackbar] = useState({ open: false, message: '' });
  const [moveTargetQuestion, setMoveTargetQuestion] = useState(null);
  const [moveTargetSectionTitle, setMoveTargetSectionTitle] = useState('');
  const [moveSubmitting, setMoveSubmitting] = useState(false);

  // Check if all questions are from the same notebook (notebook category selected)
  const isNotebookCategory = questions.length > 0 && questions.every(q => q.isNotebookExample);
  const notebookTitle = isNotebookCategory && questions.length > 0 
    ? questions[0].notebookTitle || questions[0].category 
    : null;
  const notebookId = isNotebookCategory && questions.length > 0 
    ? questions[0].notebookId 
    : null;

  // Fetch notebook outline to get section order（AbortController 避免重复请求）
  useEffect(() => {
    if (!notebookId) {
      setNotebookOutline(null);
      return;
    }
    const ctrl = new AbortController();
    (async () => {
      try {
        const response = await axios.get(`/notes/${notebookId}`, { signal: ctrl.signal });
        if (response.data && response.data.outline) {
          setNotebookOutline(response.data.outline);
        }
      } catch (error) {
        if (error?.code === 'ERR_CANCELED') return;
        console.warn('Failed to fetch notebook outline:', error);
        setNotebookOutline(null);
      }
    })();
    return () => ctrl.abort();
  }, [notebookId]);

  // 搜索过滤：按标题或题目内容
  const filteredQuestions = useMemo(() => {
    if (!searchQuery.trim()) return questions;
    const q = searchQuery.trim().toLowerCase();
    return questions.filter(
      (question) =>
        (question.title || '').toLowerCase().includes(q) ||
        (question.question || '').toLowerCase().includes(q)
    );
  }, [questions, searchQuery]);

  // Group notebook questions by section（使用过滤后的列表）
  const getGroupedBySection = (list) => {
    const grouped = {};
    (list || questions).forEach((q) => {
      const sectionTitle = q.sectionTitle || '其他';
      if (!grouped[sectionTitle]) grouped[sectionTitle] = [];
      grouped[sectionTitle].push(q);
    });
    return grouped;
  };

  const groupedBySection = getGroupedBySection(filteredQuestions);

  // 按题目类型分组（用于「按题目类型」Tab）
  const QUESTION_TYPE_LABELS = {
    multiple_choice: '选择题',
    short_answer: '简答题',
    proof: '证明题',
    code: '代码题',
    code_tracing: '代码追踪题',
  };
  const QUESTION_TYPE_ORDER = ['multiple_choice', 'short_answer', 'proof', 'code', 'code_tracing'];

  const getGroupedByType = (list) => {
    const grouped = {};
    (list || questions).forEach((q) => {
      const type = q.questionType || '其他';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(q);
    });
    return grouped;
  };

  const groupedByType = getGroupedByType(filteredQuestions);
  const typeKeys = QUESTION_TYPE_ORDER.filter(k => groupedByType[k]?.length).concat(
    Object.keys(groupedByType).filter(k => !QUESTION_TYPE_ORDER.includes(k))
  );

  // 按难度分组（用于「按难度筛选」Tab）
  const DIFFICULTY_LABELS = { easy: '简单', medium: '中等', hard: '困难', exam: '考试原题' };
  const DIFFICULTY_ORDER = ['easy', 'medium', 'hard', 'exam'];
  const getGroupedByDifficulty = (list) => {
    const grouped = {};
    (list || questions).forEach((q) => {
      const d = q.difficulty || 'easy';
      if (!grouped[d]) grouped[d] = [];
      grouped[d].push(q);
    });
    return grouped;
  };
  const groupedByDifficulty = getGroupedByDifficulty(filteredQuestions);
  const difficultyKeys = DIFFICULTY_ORDER.filter(k => groupedByDifficulty[k]?.length).concat(
    Object.keys(groupedByDifficulty).filter(k => !DIFFICULTY_ORDER.includes(k))
  );
  
  // Sort section keys by notebook outline order (outline.outlines 的键顺序即章节顺序)
  const getSortedSectionKeys = () => {
    const keys = Object.keys(groupedBySection);
    const outlineSectionOrder = notebookOutline?.outlines && typeof notebookOutline.outlines === 'object'
      ? Object.keys(notebookOutline.outlines)
      : null;

    if (!outlineSectionOrder || outlineSectionOrder.length === 0) {
      return keys;
    }

    // 按 outline.outlines 中的出现顺序排序
    return keys.sort((a, b) => {
      const indexA = outlineSectionOrder.indexOf(a);
      const indexB = outlineSectionOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  };

  const sectionKeys = getSortedSectionKeys();

  // Handle add exercise button click
  const handleAddExerciseClick = (event, sectionTitle) => {
    event.stopPropagation(); // Prevent accordion toggle
    setSelectedSectionForMenu(sectionTitle);
    setAddExerciseDialogOpen(true);
  };

  // Handle add exercise dialog close
  const handleAddExerciseDialogClose = () => {
    setAddExerciseDialogOpen(false);
    setSelectedSectionForMenu(null);
  };

  // Fetch section statistics for all sections in one request（AbortController 避免重复请求）
  useEffect(() => {
    if (!isNotebookCategory || !selectedCourse || !notebookId) {
      setSectionStats({});
      return;
    }
    const ctrl = new AbortController();
    (async () => {
      try {
        const response = await axios.get('/teacher/stats/notebook-sections', {
          params: { notebookId, courseTemplateId: parseInt(selectedCourse.id, 10) },
          signal: ctrl.signal
        });
        setSectionStats(response.data || {});
      } catch (error) {
        if (error?.code === 'ERR_CANCELED') return;
        console.warn('Failed to fetch notebook section stats:', error);
        setSectionStats({});
      }
    })();
    return () => ctrl.abort();
  }, [isNotebookCategory, selectedCourse, notebookId]);

  // 拉取每题答题统计：几人做了、正确率（教师列表用，后端数据）
  const fetchQuestionStats = async (signal) => {
    if (!filteredQuestions.length) {
      setQuestionStats({});
      return;
    }
    try {
      const questionIds = filteredQuestions
        .map((q) => {
          const raw = q.id ?? q._id;
          if (raw == null) return null;
          if (typeof raw === 'string' && raw.startsWith('notebook_')) return raw;
          if (typeof raw === 'number' || (typeof raw === 'string' && /^\d+$/.test(raw))) {
            return q.isNotebookExample ? `notebook_${raw}` : String(raw);
          }
          return String(raw);
        })
        .filter(Boolean);
      if (!questionIds.length) {
        setQuestionStats({});
        return;
      }
      const res = await axios.post(
        '/teacher/question-stats-batch',
        { questionIds, courseTemplateId: selectedCourse?.id ?? undefined },
        { signal }
      );
      setQuestionStats(res.data || {});
    } catch (err) {
      if (err?.code === 'ERR_CANCELED') return;
      console.warn('Failed to fetch question stats:', err);
      setQuestionStats({});
    }
  };

  // 打开「移动到章节」对话框
  const handleOpenMoveToSection = (question) => {
    setMoveTargetQuestion(question);
    setMoveTargetSectionTitle(question.sectionTitle || '');
    setMoveSubmitting(false);
  };

  const handleCloseMoveDialog = () => {
    if (!moveSubmitting) {
      setMoveTargetQuestion(null);
      setMoveTargetSectionTitle('');
    }
  };

  // 确认移动题目到选定章节
  const handleConfirmMoveToSection = async () => {
    if (!moveTargetQuestion || !moveTargetSectionTitle || !onQuestionUpdate) return;
    const exampleId = String(moveTargetQuestion.id ?? moveTargetQuestion._id).replace(/^notebook_/, '');
    if (!exampleId) return;
    setMoveSubmitting(true);
    try {
      await axios.put(`/notebook-examples/${exampleId}`, { sectionTitle: moveTargetSectionTitle });
      onQuestionUpdate({ ...moveTargetQuestion, sectionTitle: moveTargetSectionTitle });
      setDifficultySnackbar({ open: true, message: '已移动到该章节' });
      setMoveTargetQuestion(null);
      setMoveTargetSectionTitle('');
    } catch (err) {
      console.warn('Failed to move example:', err);
      setDifficultySnackbar({
        open: true,
        message: err?.response?.data?.error || '移动失败，请重试'
      });
    } finally {
      setMoveSubmitting(false);
    }
  };

  // 笔记本章节列表（用于移动题目下拉）：优先 outline，否则用题目中出现的章节
  const notebookSectionTitles = useMemo(() => {
    if (notebookOutline?.outlines && typeof notebookOutline.outlines === 'object') {
      const fromOutline = Object.keys(notebookOutline.outlines).filter(
        (k) => typeof notebookOutline.outlines[k] === 'string'
      );
      if (fromOutline.length > 0) return fromOutline;
    }
    return Object.keys(groupedBySection).sort();
  }, [notebookOutline, groupedBySection]);

  // 表格内直接改难度（选择即保存，普通题 PUT /questions/:id，笔记本例题 PUT /notebook-examples/:exampleId）
  const handleDifficultyChange = async (question, newDifficulty) => {
    if (!onQuestionUpdate) return;
    try {
      if (question.isNotebookExample) {
        const exampleId = String(question.id ?? question._id).replace(/^notebook_/, '');
        if (!exampleId) return;
        await axios.put(`/notebook-examples/${exampleId}`, { difficulty: newDifficulty });
        onQuestionUpdate({ ...question, difficulty: newDifficulty });
      } else {
        const questionId = question._id ?? question.id;
        if (!questionId) return;
        const res = await axios.put(`/questions/${questionId}`, { difficulty: newDifficulty });
        if (res.data?.question) onQuestionUpdate(res.data.question);
      }
      setDifficultySnackbar({ open: true, message: '难度已保存' });
    } catch (err) {
      console.warn('Failed to update difficulty:', err);
      setDifficultySnackbar({ open: true, message: '保存失败，请重试' });
    }
  };

  // 题目列表或搜索变化时重新拉取统计（AbortController 避免重复请求）
  const questionIdsKey = useMemo(
    () => filteredQuestions.map((q) => String(q.id ?? q._id)).sort().join(','),
    [filteredQuestions]
  );
  useEffect(() => {
    const ctrl = new AbortController();
    fetchQuestionStats(ctrl.signal);
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionIdsKey, searchQuery, selectedCourse?.id]);

  // Calculate statistics for the notebook
  const totalQuestions = questions.length;

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Only show title and sort when not viewing a notebook */}
      {!notebookTitle && !selectedNotebook && (
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography component="h2" variant="h6">
              全部题目
            </Typography>
            <TextField
              select
              size="small"
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value)}
              sx={{ minWidth: 150 }}
              label="排序方式"
            >
              <MenuItem value="questionNumber">按题号</MenuItem>
              <MenuItem value="difficulty">按难度</MenuItem>
              <MenuItem value="title">按标题</MenuItem>
            </TextField>
          </Stack>
        </Box>
      )}

      {notebookTitle || selectedNotebook ? (
        /* Show notebook questions grouped by section */
        questions.length === 0 ? (
          <Card variant="outlined">
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                该笔记本暂无练习题
              </Typography>
            </Box>
          </Card>
        ) : (
          <Box>
            {/* Notebook Overview Poster */}
            <Paper
              sx={{
                position: 'relative',
                backgroundColor: 'grey.800',
                color: '#fff',
                mb: 4,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundImage: `url(https://picsum.photos/seed/${encodeURIComponent(notebookTitle || 'notebook')}/1200/400)`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <img 
                style={{ display: 'none' }} 
                src={`https://picsum.photos/seed/${encodeURIComponent(notebookTitle || 'notebook')}/1200/400`} 
                alt={notebookTitle || 'notebook'} 
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  right: 0,
                  left: 0,
                  backgroundColor: 'rgba(0,0,0,.4)',
                }}
              />
              <Grid container>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      position: 'relative',
                      p: { xs: 3, md: 6 },
                      pr: { md: 0 },
                    }}
                  >
                    <Typography
                      component="h1"
                      variant="h3"
                      color="inherit"
                      gutterBottom
                      fontWeight="bold"
                    >
                      {notebookTitle || '笔记本'}
                    </Typography>
                    <Typography
                      variant="h5"
                      color="inherit"
                      sx={{
                        opacity: 0.95,
                        mt: 2,
                        fontWeight: 500,
                      }}
                    >
                      总题数 {totalQuestions} 题
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={viewMode}
                onChange={(_, v) => setViewMode(v)}
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Tab label="按章节" value="section" />
                <Tab label="按题目类型" value="type" />
                <Tab label="按难度筛选" value="difficulty" />
              </Tabs>
              <TextField
                size="small"
                placeholder="搜索题目"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 160, flexShrink: 0 }}
                inputProps={{ 'aria-label': '搜索题目' }}
              />
            </Box>

            {viewMode === 'section' && sectionKeys.map((sectionTitle) => {
              const sectionQuestions = groupedBySection[sectionTitle];
              // Use notebook title and section title as key
              const sectionKey = `${notebookTitle || selectedNotebook}_${sectionTitle}`;
              const isSectionExpanded = expandedSections[sectionKey] || false;
              
              return (
                <Accordion
                  key={sectionKey}
                  expanded={isSectionExpanded}
                  onChange={() => onSectionToggle(sectionKey)}
                  sx={{ 
                    mb: 1.5,
                    borderRadius: 2,
                    boxShadow: isSectionExpanded 
                      ? '0 2px 8px rgba(0,0,0,0.1)' 
                      : '0 1px 3px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: isSectionExpanded ? 'primary.main' : 'divider',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                      borderColor: 'primary.light',
                    },
                    '&:before': {
                      display: 'none',
                    },
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                    sx={{
                      minHeight: 56,
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      '&.Mui-expanded': {
                        minHeight: 56,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 600, 
                            color: isSectionExpanded ? 'primary.main' : 'text.primary',
                            transition: 'color 0.2s ease-in-out',
                          }}
                        >
                          {sectionTitle}
                        </Typography>
                        {!isSectionExpanded && sectionStats[sectionTitle] && (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              班级统计：{sectionStats[sectionTitle].totalStudents || 0} 名学生
                              {sectionStats[sectionTitle].attemptedStudents > 0 && 
                                ` · ${sectionStats[sectionTitle].attemptedStudents} 名已答题`}
                            </Typography>
                            {sectionStats[sectionTitle].averageQuestionsAttempted > 0 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.5 }}
                              >
                                平均做了 {sectionStats[sectionTitle].averageQuestionsAttempted.toFixed(1)} / {sectionStats[sectionTitle].totalQuestions || sectionQuestions.length} 题
                              </Typography>
                            )}
                            {sectionStats[sectionTitle].averageScore > 0 && (
                              <Typography
                                variant="caption"
                                color={sectionStats[sectionTitle].averageScore >= 7 ? 'success.main' : sectionStats[sectionTitle].averageScore >= 5 ? 'warning.main' : 'error.main'}
                                sx={{ display: 'block', fontWeight: 500, mt: 0.5 }}
                              >
                                平均分：{sectionStats[sectionTitle].averageScore.toFixed(1)}/10
                                {sectionStats[sectionTitle].averagePassRate > 0 && 
                                  ` · 平均通过率：${sectionStats[sectionTitle].averagePassRate}%`}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`${sectionQuestions.length} 题`}
                          size="small"
                          color={isSectionExpanded ? 'primary' : 'default'}
                          variant={isSectionExpanded ? 'filled' : 'outlined'}
                          sx={{
                            fontWeight: 500,
                            transition: 'all 0.2s ease-in-out',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => handleAddExerciseClick(e, sectionTitle)}
                          sx={{
                            color: 'primary.main',
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'white',
                            },
                          }}
                          title="添加题目"
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, py: 3, backgroundColor: 'background.default' }}>
                    <QuestionList
                      questions={sectionQuestions}
                      sortBy={sortBy}
                      showActions={true}
                      isNotebookExample={isNotebookCategory}
                      onViewStudents={onViewStudents}
                      onDelete={onDelete}
                      onQuestionClick={onQuestionClick}
                      questionStats={questionStats}
                      onDifficultyChange={onQuestionUpdate ? handleDifficultyChange : undefined}
                      onMoveToSection={notebookId && onQuestionUpdate ? handleOpenMoveToSection : undefined}
                    />
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {viewMode === 'type' && typeKeys.map((questionType) => {
              const typeQuestions = groupedByType[questionType];
              const typeLabel = QUESTION_TYPE_LABELS[questionType] || questionType;
              const typeKey = `type_${notebookTitle || selectedNotebook}_${questionType}`;
              const isTypeExpanded = expandedSections[typeKey] || false;

              return (
                <Accordion
                  key={typeKey}
                  expanded={isTypeExpanded}
                  onChange={() => onSectionToggle(typeKey)}
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    boxShadow: isTypeExpanded
                      ? '0 2px 8px rgba(0,0,0,0.1)'
                      : '0 1px 3px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: isTypeExpanded ? 'primary.main' : 'divider',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                      borderColor: 'primary.light',
                    },
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                    sx={{
                      minHeight: 56,
                      py: 1.5,
                      px: 2,
                      '&:hover': { backgroundColor: 'action.hover' },
                      '&.Mui-expanded': {
                        minHeight: 56,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: isTypeExpanded ? 'primary.main' : 'text.primary',
                            transition: 'color 0.2s ease-in-out',
                          }}
                        >
                          {typeLabel}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${typeQuestions.length} 题`}
                        size="small"
                        color={isTypeExpanded ? 'primary' : 'default'}
                        variant={isTypeExpanded ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 500, transition: 'all 0.2s ease-in-out' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, py: 3, backgroundColor: 'background.default' }}>
                    <QuestionList
                      questions={typeQuestions}
                      sortBy={sortBy}
                      showActions={true}
                      isNotebookExample={isNotebookCategory}
                      onViewStudents={onViewStudents}
                      onDelete={onDelete}
                      onQuestionClick={(q, ctx) => onQuestionClick(q, { ...(ctx || {}), fromView: 'type' })}
                      questionStats={questionStats}
                      onDifficultyChange={onQuestionUpdate ? handleDifficultyChange : undefined}
                      onMoveToSection={notebookId && onQuestionUpdate ? handleOpenMoveToSection : undefined}
                    />
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {viewMode === 'difficulty' && difficultyKeys.map((difficultyKey) => {
              const difficultyQuestions = groupedByDifficulty[difficultyKey];
              const difficultyLabel = DIFFICULTY_LABELS[difficultyKey] || difficultyKey;
              const difficultyKeyId = `difficulty_${notebookTitle || selectedNotebook}_${difficultyKey}`;
              const isDifficultyExpanded = expandedSections[difficultyKeyId] || false;

              return (
                <Accordion
                  key={difficultyKeyId}
                  expanded={isDifficultyExpanded}
                  onChange={() => onSectionToggle(difficultyKeyId)}
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    boxShadow: isDifficultyExpanded
                      ? '0 2px 8px rgba(0,0,0,0.1)'
                      : '0 1px 3px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: isDifficultyExpanded ? 'primary.main' : 'divider',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                      borderColor: 'primary.light',
                    },
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                    sx={{
                      minHeight: 56,
                      py: 1.5,
                      px: 2,
                      '&:hover': { backgroundColor: 'action.hover' },
                      '&.Mui-expanded': {
                        minHeight: 56,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: isDifficultyExpanded ? 'primary.main' : 'text.primary',
                            transition: 'color 0.2s ease-in-out',
                          }}
                        >
                          {difficultyLabel}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${difficultyQuestions.length} 题`}
                        size="small"
                        color={isDifficultyExpanded ? 'primary' : 'default'}
                        variant={isDifficultyExpanded ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 500, transition: 'all 0.2s ease-in-out' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, py: 3, backgroundColor: 'background.default' }}>
                    <QuestionList
                      questions={difficultyQuestions}
                      sortBy={sortBy}
                      showActions={true}
                      isNotebookExample={isNotebookCategory}
                      onViewStudents={onViewStudents}
                      onDelete={onDelete}
                      onQuestionClick={(q, ctx) => onQuestionClick(q, { ...(ctx || {}), fromView: 'difficulty' })}
                      questionStats={questionStats}
                      onDifficultyChange={onQuestionUpdate ? handleDifficultyChange : undefined}
                      onMoveToSection={notebookId && onQuestionUpdate ? handleOpenMoveToSection : undefined}
                    />
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )
      ) : (
        /* Show all questions (regular + notebook) */
        questions.length === 0 ? (
          <Card variant="outlined">
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                暂无题目
              </Typography>
            </Box>
          </Card>
        ) : (
          <QuestionList
            questions={questions}
            sortBy={sortBy}
            showActions={true}
            isNotebookExample={questions.length > 0 && questions.every((q) => q.isNotebookExample)}
            onViewStudents={onViewStudents}
            onDelete={onDelete}
            onQuestionClick={onQuestionClick}
            questionStats={questionStats}
            onDifficultyChange={onQuestionUpdate ? handleDifficultyChange : undefined}
            onMoveToSection={notebookId && onQuestionUpdate ? handleOpenMoveToSection : undefined}
          />
        )
      )}

      {/* Add Exercise Dialog */}
      {notebookId && selectedSectionForMenu && (
        <AddExerciseDialog
          open={addExerciseDialogOpen}
          onClose={handleAddExerciseDialogClose}
          notebookId={notebookId}
          sectionTitle={selectedSectionForMenu}
          onSuccess={() => {
            handleAddExerciseDialogClose();
            // Refresh the page to show the new question
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }}
        />
      )}

      {/* 移动到章节对话框 */}
      <Dialog open={Boolean(moveTargetQuestion)} onClose={handleCloseMoveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>移动到章节</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="move-section-label">目标章节</InputLabel>
            <Select
              labelId="move-section-label"
              value={moveTargetSectionTitle}
              label="目标章节"
              onChange={(e) => setMoveTargetSectionTitle(e.target.value)}
            >
              {notebookSectionTitles.map((st) => (
                <MenuItem key={st} value={st}>{st}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMoveDialog} disabled={moveSubmitting}>取消</Button>
          <Button variant="contained" onClick={handleConfirmMoveToSection} disabled={moveSubmitting || !moveTargetSectionTitle}>
            {moveSubmitting ? '移动中…' : '确定'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 难度选择即保存后的提示 */}
      <Snackbar
        open={difficultySnackbar.open}
        autoHideDuration={2000}
        onClose={() => setDifficultySnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={difficultySnackbar.message === '难度已保存' ? 'success' : 'error'} onClose={() => setDifficultySnackbar((s) => ({ ...s, open: false }))}>
          {difficultySnackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default TeacherQuestionsTab;
